import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { FileText, RefreshCw, Search, Download, Send, MapPin, PenLine, Save } from "lucide-react";
import { Button } from "../components/ui/button";

// A per-customer PDF listing the machines we hold for them, for people outside
// the farm -- the customer's bank, customs, their accountant. The document says
// in its own words that the company and every machine are in the UAE, so pick
// customers here and send; there is nothing to fill in per document.
const MachineStatements = () => {
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState("");
  // Uploaded once, then applied to every statement automatically.
  const [sig, setSig] = useState({ name: "", title: "", place: "Dubai, United Arab Emirates",
                                   signature_b64: "" });
  const [sigOpen, setSigOpen] = useState(false);
  // Off by default -- a serial is the one field on here a customer could take
  // elsewhere, and most statements only need to show what is hosted and where.
  const [withSerials, setWithSerials] = useState(false);
  // What the PDF should call this customer. Their account name is often a short
  // handle like "mhdwk", which is not what belongs on a document going to a bank.
  const [names, setNames] = useState({});
  const [savingSig, setSavingSig] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, n, g, sn] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/customers/machine-counts`),
        axios.get(`${API}/statement-signature`, { params: { include_images: true } }),
        axios.get(`${API}/statement-names`),
      ]);
      setCustomers(c.data || []);
      setCounts(n.data || {});
      setSig((prev) => ({ ...prev, ...(g.data || {}) }));
      setNames(sn.data || {});
    } catch {
      toast.error("Could not load customers");
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const openPdf = async (cust) => {
    setBusy(cust.id);
    try {
      // Fetch as a blob so Chrome doesn't block the cross-origin download
      // (frontend on wkbeast.org, backend on onrender.com).
      const resp = await axios.get(`${API}/customers/${cust.id}/machines-pdf`,
        { params: { include_serials: withSerials }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not build the PDF");
    }
    setBusy("");
  };

  const sendOne = async (cust) => {
    const n = counts[cust.id] ?? 0;
    if (!window.confirm(`Send ${cust.name} a statement of their ${n} machine(s) on WhatsApp?`)) return;
    setBusy(cust.id);
    try {
      const r = await axios.post(`${API}/whatsapp/send-machines-pdf`,
        { customer_id: cust.id, include_serials: withSerials });
      if (r.data?.success) toast.success(`Sent to ${cust.name} — ${r.data.machines} machine(s)`);
      else toast.error(r.data?.error || "WhatsApp did not accept it");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    }
    setBusy("");
  };

  const sendPicked = async () => {
    const chosen = customers.filter((c) => picked.includes(c.id));
    if (!chosen.length) return;
    if (!window.confirm(`Send a machine statement on WhatsApp to ${chosen.length} customer(s)?`)) return;
    setBusy("bulk");
    let ok = 0;
    const failed = [];
    // One at a time: Green API rejects bursts, and a half-sent batch is worse
    // than a slow one. Names of the failures are reported, not just a count.
    for (const c of chosen) {
      try {
        const r = await axios.post(`${API}/whatsapp/send-machines-pdf`,
          { customer_id: c.id, include_serials: withSerials });
        if (r.data?.success) ok += 1; else failed.push(c.name);
      } catch {
        failed.push(c.name);
      }
    }
    setBusy("");
    setPicked([]);
    if (failed.length) toast.error(`Sent ${ok}. Failed: ${failed.join(", ")}`);
    else toast.success(`Sent to ${ok} customer(s)`);
  };

  const pickImage = (field) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_500_000) {
      toast.error("Image too big — use one under 1.5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSig((p) => ({ ...p, [field]: String(reader.result) }));
    reader.readAsDataURL(file);
  };

  const saveSig = async () => {
    setSavingSig(true);
    try {
      // The saved copy comes back with the paper stripped out of it, so the
      // preview shows what the PDF will actually print, not the raw photo.
      const r = await axios.put(`${API}/statement-signature`, {
        name: sig.name, title: sig.title, place: sig.place,
        signature_b64: sig.signature_b64 || "",
      });
      setSig((p) => ({ ...p, ...(r.data || {}) }));
      toast.success("Saved — every statement is signed with this from now on");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the signature");
    }
    setSavingSig(false);
  };

  const saveName = async (cust) => {
    const name = (names[cust.id] || "").trim();
    if (name === (cust._savedName ?? "")) return;
    try {
      await axios.put(`${API}/statement-names/${cust.id}`, { name });
      cust._savedName = name;
      toast.success(name ? `Statements will say "${name}"` : `Back to "${cust.name}"`);
    } catch {
      toast.error("Could not save the name");
    }
  };

  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const filtered = query.trim()
    ? customers.filter((c) => {
        const q = query.toLowerCase();
        return (c.name || "").toLowerCase().includes(q) || (c.phone || "").includes(q);
      })
    : customers;

  const input = "w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#00E054]";

  return (
    <div className="space-y-6" data-testid="machine-statements-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText size={24} className="text-[#00E054]" /> Machine Statements
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            A PDF listing a customer's machines. States clearly that the company and the farm are in the UAE.
            Type a full name to print instead of the account name — it saves when you click away.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-[#27272A] text-gray-300" onClick={fetchAll}
                  data-testid="btn-refresh-statements">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button className="bg-[#00E054] hover:bg-[#00E054]/80 text-black font-bold"
                  disabled={!picked.length || busy === "bulk"} onClick={sendPicked}
                  data-testid="btn-send-picked">
            <Send size={14} className="mr-1" />
            {busy === "bulk" ? "Sending…" : `Send to selected (${picked.length})`}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-[#27272A] bg-[#0A0A0A] px-3">
        <MapPin size={14} className="text-[#00E054]" />
        <p className="text-xs text-gray-400 py-2">
          Every statement says: WKBeast Mining Farm is established and operating in the United Arab
          Emirates, and hosts machines only at its UAE facility.
        </p>
      </div>

      <div className="rounded-xl border border-[#27272A] bg-[#0A0A0A]">
        <button className="w-full flex items-center justify-between px-4 py-3 text-left"
                onClick={() => setSigOpen((o) => !o)} data-testid="toggle-signature">
          <span className="text-sm text-white flex items-center gap-2">
            <PenLine size={14} className="text-[#00E054]" /> Signature
            <span className={`text-xs ${sig.signature_b64 ? "text-[#00E054]" : "text-yellow-400"}`}>
              {sig.signature_b64 ? "· signed automatically" : "· not set — statements print a blank line"}
            </span>
          </span>
          <span className="text-gray-500 text-xs">{sigOpen ? "Hide" : "Edit"}</span>
        </button>
        {sigOpen && (
          <div className="px-4 pb-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="text-xs text-gray-400">Name</label>
                <input className={input} value={sig.name || ""} placeholder="Walid K."
                       onChange={(e) => setSig({ ...sig, name: e.target.value })}
                       data-testid="sig-name" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Title</label>
                <input className={input} value={sig.title || ""} placeholder="General Manager"
                       onChange={(e) => setSig({ ...sig, title: e.target.value })}
                       data-testid="sig-title" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Place</label>
                <input className={input} value={sig.place || ""} placeholder="Dubai, United Arab Emirates"
                       onChange={(e) => setSig({ ...sig, place: e.target.value })}
                       data-testid="sig-place" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400">
                Signature — photograph it on white paper, the page gets removed for you
              </label>
              <div className="flex items-center gap-3 mt-1">
                <input type="file" accept="image/*" onChange={pickImage("signature_b64")}
                       className="text-xs text-gray-400" data-testid="file-signature" />
                {sig.signature_b64 && (
                  <>
                    <img src={sig.signature_b64} alt="signature"
                         className="h-10 bg-white rounded px-1" />
                    <button className="text-xs text-red-400"
                            onClick={() => setSig({ ...sig, signature_b64: "" })}>Remove</button>
                  </>
                )}
              </div>
            </div>
            <Button className="bg-[#00E054] hover:bg-[#00E054]/80 text-black font-bold"
                    disabled={savingSig} onClick={saveSig} data-testid="save-signature">
              <Save size={14} className="mr-1" /> {savingSig ? "Saving…" : "Save signature"}
            </Button>
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer w-fit">
        <input type="checkbox" checked={withSerials}
               onChange={(e) => setWithSerials(e.target.checked)}
               data-testid="toggle-serials" />
        Include serial numbers
        <span className="text-xs text-gray-500">
          {withSerials ? "— shown in the PDF" : "— left out of the PDF"}
        </span>
      </label>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input className={`${input} pl-9`} placeholder="Search customer or phone"
               value={query} onChange={(e) => setQuery(e.target.value)} data-testid="statement-search" />
      </div>

      <div className="rounded-xl border border-[#27272A] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-gray-400">
            <tr>
              <th className="p-3 w-10"></th>
              <th className="p-3 text-left">Account</th>
              <th className="p-3 text-left">Name on the statement</th>
              <th className="p-3 text-left">Phone</th>
              <th className="p-3 text-right">Machines</th>
              <th className="p-3 text-right">Statement</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && !filtered.length && (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500">No customers</td></tr>
            )}
            {!loading && filtered.map((c) => {
              const n = counts[c.id] ?? 0;
              return (
                <tr key={c.id} className="border-t border-[#27272A] text-gray-200">
                  <td className="p-3">
                    <input type="checkbox" checked={picked.includes(c.id)}
                           onChange={() => toggle(c.id)} disabled={!n}
                           data-testid={`pick-${c.id}`} />
                  </td>
                  <td className="p-3 font-medium text-white">{c.name}</td>
                  <td className="p-3">
                    <input className={`${input} min-w-[190px]`} placeholder={c.name}
                           value={names[c.id] ?? ""}
                           onChange={(e) => setNames({ ...names, [c.id]: e.target.value })}
                           onBlur={() => saveName(c)}
                           onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                           data-testid={`name-${c.id}`} />
                  </td>
                  <td className="p-3 text-gray-400">{c.phone || "—"}</td>
                  <td className={`p-3 text-right ${n ? "text-[#00E054]" : "text-gray-600"}`}>{n}</td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" className="border-[#27272A] text-gray-300"
                              disabled={busy === c.id} onClick={() => openPdf(c)}
                              data-testid={`pdf-${c.id}`}>
                        <Download size={13} className="mr-1" /> PDF
                      </Button>
                      <Button size="sm" className="bg-[#00E054] hover:bg-[#00E054]/80 text-black font-bold"
                              disabled={busy === c.id || !c.phone} onClick={() => sendOne(c)}
                              data-testid={`send-${c.id}`}>
                        <Send size={13} className="mr-1" /> Send
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MachineStatements;
